import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { PROPERTY_CREATION_STEP_SLUGS, PropertyDto, PropertyId } from '@workspace/data-types';

export enum OwnerJourneyStepKey {
  CREATION = 'creation',
  MANDATE = 'mandate',
  OWNERSHIP = 'ownership',
}

export interface OwnerJourneyStepItem {
  key: OwnerJourneyStepKey;
  label: string;
  icon: string;
  isOptional?: boolean;
}

export type OwnerJourneyStepType = OwnerJourneyStepKey | 'creation' | 'mandate' | 'ownership' | number;

@Component({
  selector: 'etxe-owner-journey-stepper',
  imports: [CommonModule, MatIconModule, RouterLink],
  templateUrl: './owner-journey-stepper.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EtxeOwnerJourneyStepperComponent {
  currentStep = input.required<OwnerJourneyStepType>();
  propertyId = input<PropertyId | string | undefined>();
  property = input<PropertyDto | null>();

  readonly steps: OwnerJourneyStepItem[] = [
    {
      key: OwnerJourneyStepKey.CREATION,
      label: 'Création du bien',
      icon: 'home',
    },
    {
      key: OwnerJourneyStepKey.MANDATE,
      label: 'Définition du mandat',
      icon: 'handshake',
    },
    {
      key: OwnerJourneyStepKey.OWNERSHIP,
      label: 'Justificatif de propriété',
      icon: 'verified_user',
      isOptional: true,
    },
  ];

  currentStepIndex = computed(() => {
    const step = this.currentStep();
    if (typeof step === 'number') return step;
    switch (step) {
      case OwnerJourneyStepKey.CREATION:
        return 0;
      case OwnerJourneyStepKey.MANDATE:
        return 1;
      case OwnerJourneyStepKey.OWNERSHIP:
        return 2;
      default:
        return 0;
    }
  });

  currentStepNumber = computed(() => this.currentStepIndex() + 1);

  isStepAccessible(stepIndex: number): boolean {
    if (stepIndex === 0) return true;

    const prop = this.property();
    if (!prop?.farthestStepReached) return false;

    const reachedIndex = PROPERTY_CREATION_STEP_SLUGS.indexOf(prop.farthestStepReached);

    if (stepIndex === 1) {
      return this.currentStepIndex() >= 1 || reachedIndex >= PROPERTY_CREATION_STEP_SLUGS.indexOf('mandate');
    }
    if (stepIndex === 2) {
      return (
        this.currentStepIndex() >= 2 ||
        reachedIndex >= PROPERTY_CREATION_STEP_SLUGS.indexOf('ownership-document') ||
        prop.farthestStepReached === 'completed'
      );
    }
    return false;
  }

  isStepCompleted(stepIndex: number): boolean {
    const prop = this.property();
    if (!prop?.farthestStepReached) return false;

    const reachedIndex = PROPERTY_CREATION_STEP_SLUGS.indexOf(prop.farthestStepReached);

    if (stepIndex === 0) {
      return reachedIndex >= PROPERTY_CREATION_STEP_SLUGS.indexOf('mandate');
    }
    if (stepIndex === 1) {
      return (
        reachedIndex >= PROPERTY_CREATION_STEP_SLUGS.indexOf('ownership-document') ||
        prop.farthestStepReached === 'completed'
      );
    }
    if (stepIndex === 2) {
      return (
        prop.farthestStepReached === 'completed' || !!prop.documents?.some((doc) => doc.type === 'ownership')
      );
    }
    return false;
  }

  getStepLink(key: OwnerJourneyStepKey): string[] {
    const id = this.propertyId() || this.property()?._id;
    if (!id) return ['/properties/creation'];

    const idStr = String(id);
    switch (key) {
      case OwnerJourneyStepKey.CREATION:
        return ['/properties', idStr, 'creation'];
      case OwnerJourneyStepKey.MANDATE:
        return ['/properties', idStr, 'mandate'];
      case OwnerJourneyStepKey.OWNERSHIP:
        return ['/properties', idStr, 'documents'];
    }
  }
}
